/**
 * WATERDICHT Data Sync Service
 * Pollt elke 60 seconden op de hele minuut
 * Eén request per poll, geen dubbel werk
 */

import { getKmfcClient } from '../../lib/supabase';
import { fetchShopStatus } from '../lib/shopStatus';
import { addNotification } from '../hooks/useNotifications';

class DataSyncService {
  constructor() {
    this.isRunning = false;
    this.hasInitialData = false;
    this.listeners = new Map(); // type -> Set<callback>
    this.loadingListeners = new Set();
    this.errorListeners = new Set();
    this.pollTimeout = null;
    this.lastPollTime = 0;
    this.archiveLoadedDays = 3;
    this.knownWebvonkVersion = null;

    this.currentData = {
      orders: { new: [], active: [], all: [] },
      shopStatus: null,
      stats: null,
    };

    this.seenOrderIds = new Set(
      JSON.parse(localStorage.getItem('kmfc-seen-orders') || '[]')
    );
  }

  // Bereken volgende poll tijd (300 seconden na vorige poll)
  _getNextPollTime() {
    const now = Date.now();
    const secondsSinceLastPoll = this.lastPollTime ? (now - this.lastPollTime) / 1000 : 0;

    // Als laatste poll minder dan 300s geleden, wacht tot 300s zijn verstreken
    if (secondsSinceLastPoll < 300) {
      return new Date(this.lastPollTime + 300 * 1000);
    }

    // Anders poll nu en schedule volgende over 300s
    return new Date(now + 300 * 1000);
  }

  // Start polling elke 5 minuten
  async start() {
    if (this.isRunning) return;
    this.isRunning = true;

    this._notifyLoading(true);

    // Directe sync
    await this._fullSync();

    // Start polling elke 300s
    this._scheduleNextPoll();
  }

  // Schedule volgende poll elke 300s
  _scheduleNextPoll() {
    if (!this.isRunning) return;

    // Normaal: elke 300s pollen
    const nextPoll = new Date(Date.now() + 300 * 1000);
    const delay = 300 * 1000;

    console.log(`⏰ Volgende poll over ${Math.round(delay / 1000)}s om ${nextPoll.toLocaleTimeString('nl-NL')}`);

    this.pollTimeout = setTimeout(() => {
      this._performPoll();
      this._scheduleNextPoll(); // Schedule volgende
    }, delay);
  }

  // Doe een poll elke 300s
  async _performPoll() {
    if (!this.isRunning) return;

    const shopClosed = this.currentData.shopStatus && !this.currentData.shopStatus.isOpen;
    const hasActiveOrders = (this.currentData.orders?.active?.length || 0) > 0
      || (this.currentData.orders?.new?.length || 0) > 0;

    if (shopClosed && !hasActiveOrders) {
      console.log(`💤 Shop gesloten, geen actieve bestellingen — alleen shopStatus pollen`);
      try {
        const prevStatus = this.currentData.shopStatus;
        const shopStatus = await this._fetchShopStatus();
        this.currentData = { ...this.currentData, shopStatus };
        if (!prevStatus?.isOpen && shopStatus?.isOpen) {
          console.log('🟢 Shop is nu open — volledige sync starten');
          await this._fullSync();
        } else {
          this.listeners.get('shopStatus')?.forEach(cb => {
            try { cb(shopStatus); } catch (e) { console.error('❌ shopStatus listener fout:', e); }
          });
        }
      } catch (error) {
        console.error('❌ Light poll fout:', error);
      }
      return;
    }

    console.log(`🔄 Automatische polling om ${new Date().toLocaleTimeString('nl-NL')}`);

    try {
      await this._fullSync();
      this.lastPollTime = Date.now();
      console.log('✅ Automatische poll completed');
    } catch (error) {
      console.error('❌ Poll fout:', error);
    }
  }

  stop() {
    if (!this.isRunning) return;
    this.isRunning = false;
    this.hasInitialData = false;

    if (this.pollTimeout) {
      clearTimeout(this.pollTimeout);
      this.pollTimeout = null;
    }

    this._notifyLoading(false);
    console.log('🛑 DataSyncService gestopt');
  }

  // Volledige sync - EEN request voor alles
  async _fullSync() {
    try {
      const client = getKmfcClient();
      if (!client) throw new Error('Geen client');

      // Laad actieve orders + archief van de laatste N dagen
      const archiveCutoff = new Date(Date.now() - this.archiveLoadedDays * 24 * 60 * 60 * 1000)
        .toISOString().split('T')[0];

      const { data: allOrders, error: ordersError } = await client
        .from('orders')
        .select('id, order_code, status, company_slug, customer_name, customer_phone, customer_email, delivery_type, delivery_address, delivery_postcode, delivery_city, delivery_time, is_asap, delivery_notes, delivery_km, driving_time, delivery_fee_cents, tip_cents, total_cents, is_test, interne_notitie, placed_at, updated_at, delivered_at')
        .neq('status', 'unpaid_deleted')
        .or(`status.not.in.(bezorgd,voltooid,geannuleerd),placed_at.gte.${archiveCutoff}`)
        .order('placed_at', { ascending: false });

      if (ordersError) throw ordersError;

      // Bereken stats lokaal (geen extra requests)
      const stats = this._computeStats(allOrders || []);

      // Fetch shop status (apart request, maar klein)
      const shopStatus = await this._fetchShopStatus();

      // Versiecheck — meldt subscribers als Mijn Webvonk een update heeft uitgerold
      const newVersion = shopStatus?.webvonkVersion ?? null;
      if (newVersion && this.knownWebvonkVersion && newVersion !== this.knownWebvonkVersion) {
        console.log(`🆕 Mijn Webvonk versie gewijzigd: ${this.knownWebvonkVersion} → ${newVersion}`);
        this.listeners.get('webvonkUpdate')?.forEach(cb => {
          try { cb({ from: this.knownWebvonkVersion, to: newVersion }); } catch (e) { console.error('❌ webvonkUpdate listener fout:', e); }
        });
      }
      if (newVersion) this.knownWebvonkVersion = newVersion;

      // Update data
      const prev = this.currentData;
      this.currentData = {
        orders: this._categorizeOrders(allOrders || []),
        shopStatus,
        stats,
      };

      if (!this.hasInitialData) {
        this.hasInitialData = true;
        this.lastPollTime = Date.now();
        this._notifyLoading(false);
        console.log('✅ Initiële data geladen');
      }

      this._notifyChanged(prev, this.currentData);

    } catch (error) {
      console.error('❌ Sync fout:', error);
      const code = error?.code || error?.status || error?.message?.slice(0, 20) || 'UNKNOWN';
      this.errorListeners.forEach(cb => { try { cb(code); } catch (e) {} });
      if (!this.hasInitialData) {
        this._notifyLoading(false);
      }
    }
  }

  // Orders categoriseren
  _categorizeOrders(orders) {
    const today = new Date().toISOString().split('T')[0];
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);

    const userRoleId = (() => {
      try { return JSON.parse(localStorage.getItem('kmfc_user') || '{}')?.role?.id; } catch { return null; }
    })();
    const newOrders = orders.filter(o => {
      if (o.is_test && userRoleId !== 1) return false;
      if (o.status === 'nieuw') return true;
      return o.status === 'unpaid' && o.placed_at && new Date(o.placed_at) < fiveMinutesAgo && o.placed_at.startsWith(today);
    });

    if (newOrders.length > 0) {
      console.log(`📋 orders.new (${newOrders.length}):`, newOrders.map(o => ({ id: o.id, status: o.status, placed_at: o.placed_at, is_test: o.is_test })));
    }

    return {
      new: newOrders,
      active: orders.filter(o => !o.is_test && ['bereiding', 'klaar', 'onderweg'].includes(o.status)),
      all: orders,
    };
  }

  // Stats berekenen uit orders (geen extra request)
  _computeStats(allOrders) {
    const today = new Date().toISOString().split('T')[0];
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);

    const todayOrders = allOrders.filter(o => {
      if (!o.placed_at || o.is_test || o.status === 'geannuleerd' || o.status === 'unpaid') return false;
      return o.placed_at.split('T')[0] === today;
    });

    const readyDeliveryOrders = allOrders.filter(o => o.status === 'klaar' && !o.is_test && o.delivery_type === 'delivery');
    const paidOrders = allOrders.filter(o => ['nieuw', 'nieuw'].includes(o.status) && !o.is_test);
    const unpaidOldOrders = todayOrders.filter(o => o.status === 'unpaid' && new Date(o.placed_at) < fiveMinutesAgo);
    const preparingOrders = allOrders.filter(o => o.status === 'bereiding' && !o.is_test);
    const readyOrders = allOrders.filter(o => o.status === 'klaar' && !o.is_test);
    const outForDelivery = allOrders.filter(o => o.status === 'onderweg' && !o.is_test);
    const completedOrders = todayOrders.filter(o => ['bezorgd', 'voltooid'].includes(o.status));
    const archiveOrders = allOrders.filter(o =>
      ['bezorgd', 'voltooid'].includes(o.status) && !o.is_test && o.placed_at.split('T')[0] < today
    );

    const testOrders = allOrders.filter(o => o.is_test);

    return {
      readyForDelivery: readyDeliveryOrders.length,
      todayOrdersCount: todayOrders.length,
      tabCounts: {
        new: paidOrders.length + unpaidOldOrders.length,
        preparing: preparingOrders.length,
        ready: readyOrders.length,
        out_for_delivery: outForDelivery.length,
        completed: completedOrders.length,
        archive: archiveOrders.length,
        testNew: testOrders.filter(o => ['nieuw', 'nieuw'].includes(o.status)).length,
        testPreparing: testOrders.filter(o => o.status === 'bereiding').length,
        testReady: testOrders.filter(o => o.status === 'klaar').length,
        testOutForDeliveryOrders: testOrders.filter(o => o.status === 'onderweg').length,
        testCompletedOrders: testOrders.filter(o => ['bezorgd', 'voltooid'].includes(o.status)).length,
      },
    };
  }

  async _fetchShopStatus() {
    try {
      return await fetchShopStatus();
    } catch {
      return { isOpen: false, status: 'Unknown' };
    }
  }

  // Subscribers
  subscribe(type, callback) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type).add(callback);

    // Directe callback met huidige data
    if (this.currentData[type]) {
      callback(this.currentData[type]);
    }

    return () => this.listeners.get(type)?.delete(callback);
  }

  onLoadingChange(callback) {
    this.loadingListeners.add(callback);
    callback(this.isLoadingData());
    return () => this.loadingListeners.delete(callback);
  }

  onError(callback) {
    this.errorListeners.add(callback);
    return () => this.errorListeners.delete(callback);
  }

  // Notify helpers
  _notifyChanged(prev, curr) {
    ['orders', 'shopStatus', 'stats'].forEach(type => {
      if (JSON.stringify(prev[type]) !== JSON.stringify(curr[type])) {
        this.listeners.get(type)?.forEach(cb => {
          try { cb(curr[type]); } catch (e) { console.error(`❌ Listener fout (${type}):`, e); }
        });
      }
    });

    // Nieuwe orders detecteren (alleen na initiële load)
    if (this.hasInitialData && curr.orders?.all) {
      const newIds = [];
      for (const order of curr.orders.all) {
        if (order.status === 'nieuw' && !this.seenOrderIds.has(order.id)) {
          newIds.push(order.id);
          this.seenOrderIds.add(order.id);
          addNotification({
            type: 'new_order',
            title: 'Nieuwe bestelling',
            message: `Bestelling #${order.order_code || order.id.slice(0, 6).toUpperCase()} van ${order.customer_name || 'onbekend'}`,
            orderId: order.id,
          });
        }
      }
      if (newIds.length > 0) {
        localStorage.setItem('kmfc-seen-orders', JSON.stringify([...this.seenOrderIds].slice(-200)));
      }
    }

    // ShopStatus wijziging detecteren (alleen na initiële load, alleen automatisch)
    if (this.hasInitialData && prev.shopStatus && curr.shopStatus) {
      const wasOpen = prev.shopStatus.isOpen;
      const isNowOpen = curr.shopStatus.isOpen;
      if (wasOpen !== isNowOpen) {
        addNotification({
          type: 'shop_status',
          title: isNowOpen ? 'Winkel automatisch geopend' : 'Winkel automatisch gesloten',
          message: isNowOpen
            ? 'De winkel is automatisch opengegaan op basis van de openingstijden.'
            : `De winkel is automatisch gesloten${curr.shopStatus.reason ? `: ${curr.shopStatus.reason}` : ' op basis van de openingstijden.'}`,
        });
      }
    }
  }

  _notifyLoading(isLoading) {
    this.loadingListeners.forEach(cb => {
      try { cb(isLoading); } catch (e) { console.error('❌ Loading listener fout:', e); }
    });
  }

  // Forceer sync (bijv. na handmatige actie)
  async forceSync() {
    console.log('🔄 Force sync called');
    try {
      await this._fullSync();
      // Update lastPollTime zodat volgende automatische poll correct wordt gescheduled
      this.lastPollTime = Date.now();
      console.log('✅ Force sync completed - lastPollTime updated');
    } catch (error) {
      console.error('❌ Force sync failed:', error);
    }
  }

  async loadMoreArchive() {
    this.archiveLoadedDays += 3;
    await this._fullSync();
  }

  // Zoekt door ALLE archieforders in de DB (niet beperkt tot geladen dagen)
  async searchArchive(query) {
    const client = getKmfcClient();
    if (!client) return [];
    const q = `%${query}%`;
    const { data, error } = await client
      .from('orders')
      .select('*')
      .in('status', ['bezorgd', 'voltooid', 'geannuleerd'])
      .or(`customer_name.ilike.${q},customer_email.ilike.${q},customer_phone.ilike.${q},order_code.ilike.${q},delivery_address.ilike.${q},delivery_city.ilike.${q}`)
      .order('placed_at', { ascending: false });
    if (error) { console.error('❌ Archief zoeken fout:', error); return []; }
    return data || [];
  }

  getArchiveLoadedDays() { return this.archiveLoadedDays; }

  // Status helpers
  isActive() { return this.isRunning; }
  isLoadingData() { return this.isRunning && !this.hasInitialData; }
  hasData() { return this.hasInitialData; }
  getData(type) { return this.currentData[type]; }
}

const dataSyncService = new DataSyncService();
export default dataSyncService;